(() => {
  if (window.__seenSizeGuideLoaded) return;
  window.__seenSizeGuideLoaded = true;

  const OPEN_SELECTOR = '[data-seen-size-guide-open]';
  const DIALOG_SELECTOR = '[data-seen-size-guide]';
  const CLOSE_SELECTOR = '[data-seen-size-guide-close]';
  const TAB_SELECTOR = '[data-seen-size-guide-tab]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeDialog = null;
  let activeTrigger = null;
  let scrollY = 0;
  let closeTimer = null;
  let bodyStyles = null;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getFocusable = (dialog) =>
    Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      const style = window.getComputedStyle(element);
      return (
        element.tabIndex >= 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        !element.hidden &&
        element.getClientRects().length > 0
      );
    });

  const marketUnit = (dialog) =>
    dialog.dataset.seenSizeGuideDefaultUnit === 'in' ? 'in' : 'cm';

  const clearLegacyUnitPreference = () => {
    try {
      window.localStorage.removeItem('seen:size-guide-unit');
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes. The drawer still works.
    }
  };

  const lockPage = () => {
    const body = document.body;
    scrollY = window.scrollY || window.pageYOffset || 0;
    bodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    document.documentElement.classList.add('seen-size-guide-open');
  };

  const unlockPage = () => {
    const body = document.body;
    if (bodyStyles) {
      Object.entries(bodyStyles).forEach(([property, value]) => {
        body.style[property] = value;
      });
    }
    bodyStyles = null;
    document.documentElement.classList.remove('seen-size-guide-open');
    window.scrollTo(0, scrollY);
  };

  const convertToInches = (raw) =>
    raw.replace(/\d+(?:\.\d+)?/g, (token) => (Number.parseFloat(token) / 2.54).toFixed(1));

  const updateSwipeHints = (dialog) => {
    dialog.querySelectorAll('[data-seen-size-guide-table-wrap]').forEach((wrap) => {
      const hint = wrap.parentElement?.querySelector('[data-seen-size-guide-swipe]');
      if (!hint) return;
      const visible = wrap.getClientRects().length > 0 && wrap.scrollWidth > wrap.clientWidth + 1;
      hint.classList.toggle('is-visible', visible);
    });
  };

  const setUnit = (dialog, unit) => {
    const safeUnit = unit === 'in' ? 'in' : 'cm';
    const isInches = safeUnit === 'in';
    dialog.dataset.seenSizeGuideCurrentUnit = safeUnit;
    dialog.querySelectorAll('[data-seen-size-guide-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.seenSizeGuideUnit === safeUnit));
    });
    dialog.querySelectorAll('[data-seen-size-guide-header]').forEach((header) => {
      const label = header.dataset.label || '';
      header.textContent = `${label} (${isInches ? 'IN' : 'CM'})`;
    });
    dialog.querySelectorAll('[data-seen-size-guide-measurement]').forEach((cell) => {
      const raw = cell.dataset.cm || '';
      cell.textContent = isInches ? convertToInches(raw) : raw;
    });
    const note = dialog.querySelector('[data-seen-size-guide-unit-note]');
    if (note) {
      note.textContent = isInches
        ? 'Measurements are shown in inches.'
        : 'Measurements are shown in centimetres.';
    }
  };

  const setTab = (dialog, requestedTab) => {
    const availablePanels = Array.from(dialog.querySelectorAll('[data-seen-size-guide-panel]'));
    if (!availablePanels.length) return;
    const requestedPanel = availablePanels.find((panel) => panel.dataset.seenSizeGuidePanel === requestedTab);
    const activePanel = requestedPanel || availablePanels[0];
    const activeTab = activePanel.dataset.seenSizeGuidePanel;

    availablePanels.forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });
    dialog.querySelectorAll(TAB_SELECTOR).forEach((button) => {
      const selected = button.dataset.seenSizeGuideTab === activeTab;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    dialog.dataset.seenSizeGuideCurrentTab = activeTab;
    window.requestAnimationFrame(() => updateSwipeHints(dialog));
  };

  const openDialog = (trigger) => {
    const id = trigger.getAttribute('aria-controls');
    const dialog = id ? document.getElementById(id) : null;
    if (!dialog || !dialog.matches(DIALOG_SELECTOR)) return;

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (activeDialog && activeDialog !== dialog) closeDialog(true);

    activeDialog = dialog;
    activeTrigger = trigger;
    dialog.hidden = false;
    clearLegacyUnitPreference();
    setTab(dialog, dialog.dataset.seenSizeGuideDefaultTab || 'garment');
    setUnit(dialog, marketUnit(dialog));
    dialog.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    lockPage();

    window.requestAnimationFrame(() => {
      dialog.classList.add('is-open');
      updateSwipeHints(dialog);
      const focusTarget = dialog.querySelector('.seen-size-guide__close') || dialog.querySelector('[role="dialog"]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    });
  };

  const finishClose = (dialog, trigger) => {
    if (!dialog) return;
    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');
    unlockPage();
    if (trigger && document.contains(trigger)) {
      trigger.focus({ preventScroll: true });
    }
    if (activeDialog === dialog) activeDialog = null;
    if (activeTrigger === trigger) activeTrigger = null;
    closeTimer = null;
  };

  function closeDialog(immediate = false) {
    const dialog = activeDialog;
    const trigger = activeTrigger;
    if (!dialog) return;

    dialog.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (immediate || reducedMotion()) {
      finishClose(dialog, trigger);
      return;
    }
    closeTimer = window.setTimeout(() => finishClose(dialog, trigger), 300);
  }

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest(OPEN_SELECTOR);
    if (openButton) {
      event.preventDefault();
      openDialog(openButton);
      return;
    }

    const unitButton = event.target.closest('[data-seen-size-guide-unit]');
    if (unitButton) {
      const dialog = unitButton.closest(DIALOG_SELECTOR);
      if (dialog) setUnit(dialog, unitButton.dataset.seenSizeGuideUnit);
      return;
    }

    const tabButton = event.target.closest(TAB_SELECTOR);
    if (tabButton) {
      const dialog = tabButton.closest(DIALOG_SELECTOR);
      if (dialog) setTab(dialog, tabButton.dataset.seenSizeGuideTab);
      return;
    }

    if (event.target.closest(CLOSE_SELECTOR) && activeDialog) {
      event.preventDefault();
      closeDialog();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDialog) return;

    if (event.target.matches(TAB_SELECTOR) && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const tabs = Array.from(activeDialog.querySelectorAll(TAB_SELECTOR));
      if (!tabs.length) return;
      const currentIndex = tabs.indexOf(event.target);
      let nextIndex = currentIndex;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      event.preventDefault();
      setTab(activeDialog, tabs[nextIndex].dataset.seenSizeGuideTab);
      tabs[nextIndex].focus();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = getFocusable(activeDialog);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (activeDialog) updateSwipeHints(activeDialog);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (activeDialog && event.target.contains(activeDialog)) closeDialog(true);
  });
})();
